import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Task } from '../../types/tasks';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useSchool } from '../../contexts/SchoolContext';
import { NotificationService } from '../../services/NotificationService';

interface CreateTaskModalProps {
  onClose: () => void;
  onCreate: (task: Partial<Task>) => void;
}

interface UserOption {
  id: string;
  name: string;
  email: string;
  department?: string;
}

export default function CreateTaskModal({ onClose, onCreate }: CreateTaskModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<Task['priority']>('medium');
  const [assignedTo, setAssignedTo] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserOption[]>([]);
  const { selectedSchool } = useSchool();

  useEffect(() => {
    const loadSchoolMembers = async () => {
      if (!selectedSchool) return;

      try {
        // Get all school members
        const membersQuery = query(
          collection(db, 'school_members'),
          where('schoolId', '==', selectedSchool.id)
        );
        const memberSnapshot = await getDocs(membersQuery);
        
        // Get user details for each member
        const userPromises = memberSnapshot.docs.map(async (memberDoc) => {
          const userDoc = await getDocs(
            query(
              collection(db, 'users'),
              where('__name__', '==', memberDoc.data().userId)
            )
          );
          
          if (!userDoc.empty) {
            const userData = userDoc.docs[0].data();
            return {
              id: memberDoc.data().userId,
              name: userData.fullName || userData.displayName || userData.email?.split('@')[0] || 'Unknown User',
              email: userData.email || '',
              department: userData.department
            };
          }
          return null;
        });

        const users = (await Promise.all(userPromises)).filter((user): user is UserOption => user !== null);
        setUsers(users);
      } catch (error) {
        console.error('Error loading school members:', error);
      }
    };

    loadSchoolMembers();
  }, [selectedSchool]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) return;

    setLoading(true);
    
    const taskData = {
      title: title.trim(),
      ...(description && { description: description.trim() }),
      ...(dueDate && { dueDate }),
      ...(assignedTo.length > 0 && { assignedTo }),
      priority,
    };

    await onCreate(taskData);

    // Create notifications for assigned users, especially for high-priority tasks
    if (assignedTo.length > 0 && auth.currentUser) {
      const currentUserName = auth.currentUser.displayName || auth.currentUser.email || 'Unknown User';
      
      for (const userId of assignedTo) {
        try {
          await NotificationService.createTaskNotification(
            title.trim(),
            currentUserName,
            userId,
            priority,
            dueDate
          );
        } catch (error) {
          console.error('Error creating notification for user:', userId, error);
          // Don't fail the entire task creation if notification fails
        }
      }
    }

    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-light rounded-xl shadow-lg w-full max-w-md max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border-light">
          <h2 className="text-xl font-semibold text-text-primary">Create Task</h2>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-8rem)]">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-text-secondary mb-1">
              Title <span className="text-error-DEFAULT">*</span>
            </label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                       focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-text-secondary mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                       focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
              rows={3}
            />
          </div>

          <div>
            <label htmlFor="dueDate" className="block text-sm font-medium text-text-secondary mb-1">
              Due Date
            </label>
            <input
              id="dueDate"
              type="datetime-local"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                       focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
            />
          </div>

          <div>
            <label htmlFor="priority" className="block text-sm font-medium text-text-secondary mb-1">
              Priority
            </label>
            <select
              id="priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as Task['priority'])}
              className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                       focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div>
            <label htmlFor="assignedTo" className="block text-sm font-medium text-text-secondary mb-1">
              Assign To
            </label>
            <select
              id="assignedTo"
              multiple
              value={assignedTo}
              onChange={(e) => setAssignedTo(Array.from(e.target.selectedOptions, option => option.value))}
              className="w-full px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                       focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
              size={4}
            >
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                  {user.department ? ` - ${user.department}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-border-light">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !title.trim()}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                       transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}